import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence, useScroll, useSpring } from 'motion/react';
import { Menu, X, Globe, ChevronDown, User, Award, Building2, ArrowRight } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { useTranslation } from 'react-i18next';
import { Link, useLocation } from 'react-router-dom';

import { useRegion } from '@/contexts/RegionContext';
import { useSiteSettings } from '@/contexts/SiteSettingsContext';
import { LazyImage } from '@/components/ui/LazyImage';

export const handleDiscoveryCallClick = (href: string) => {
  if (
    href &&
    (href.includes('bit.ly/ennea-discovery') ||
      href.includes('outlook.office.com/book') ||
      href.includes('outlook.office365.com/book'))
  ) {
    if (typeof window !== 'undefined' && typeof window.gtag === 'function') {
      window.gtag('event', 'book_discovery_call', {
        link_url: href,
        page_location: window.location.href,
        transport_type: 'beacon',
      });
    }
  }
};

export function Navbar() {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const { links } = useSiteSettings();
  const [isScrolled, setIsScrolled] = useState(false);
  const [solutionsOpen, setSolutionsOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { regionCode, setRegionCode } = useRegion();

  const dropdownRef = useRef<HTMLDivElement>(null);
  const solutionsTriggerRef = useRef<HTMLButtonElement>(null);

  const { scrollYProgress } = useScroll();
  const scaleX = useSpring(scrollYProgress, {
    stiffness: 100,
    damping: 30,
    restDelta: 0.001
  });

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close menus when route changes
  useEffect(() => {
    setSolutionsOpen(false);
    setMobileMenuOpen(false);
  }, [location.pathname]);

  // Close dropdown on outside click
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        solutionsTriggerRef.current &&
        !solutionsTriggerRef.current.contains(event.target as Node)
      ) {
        setSolutionsOpen(false);
      }
    }

    if (solutionsOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [solutionsOpen]);

  // Close menus on Escape key
  useEffect(() => {
    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        if (solutionsOpen) {
          setSolutionsOpen(false);
          solutionsTriggerRef.current?.focus();
        }
        if (mobileMenuOpen) {
          setMobileMenuOpen(false);
        }
      }
    }

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [solutionsOpen, mobileMenuOpen]);

  const primaryNavLinks = [
    { label: 'Framework', href: '/framework' },
    { label: 'Evidence', href: '/evidence-and-research' },
    { label: 'Resources', href: '/resource-library' },
    { label: 'About', href: '/about' },
  ];

  const changeLanguage = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const lang = e.target.value;
    i18n.changeLanguage(lang);
    localStorage.setItem('appLang', lang);
  };

  return (
    <header
      className={`fixed top-0 w-full z-50 transition-all duration-300 bg-white border-b border-ground-deep h-[82px] flex items-center`}
    >
      <div className="w-full px-6 lg:px-12 flex items-center justify-between h-full">
        <a href="/" className="flex items-center shrink-0 z-50">
          <img 
            src="/company_logoV2.webp" 
            alt="FIVE LENS Enneagram" 
            className="h-10 md:h-12 w-auto object-contain block"
            decoding="async"
          />
        </a>

        {/* Desktop Nav */}
        <nav className="hidden md:flex items-center gap-6 lg:gap-8 h-full" aria-label="Main Navigation">
          {/* Solutions Dropdown Menu */}
          <div 
            className="h-full flex items-center"
            onMouseEnter={() => setSolutionsOpen(true)}
            onMouseLeave={() => setSolutionsOpen(false)}
          >
            <button 
              ref={solutionsTriggerRef}
              id="solutions-nav-trigger"
              type="button"
              onClick={() => setSolutionsOpen((prev) => !prev)}
              onKeyDown={(e) => {
                if (e.key === 'ArrowDown' || e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  setSolutionsOpen(true);
                }
              }}
              className={`text-[13px] font-medium uppercase tracking-widest transition-colors flex items-center gap-1.5 h-full ${
                solutionsOpen ? 'text-ink font-semibold' : 'text-ink/70 hover:text-ink'
              }`}
              aria-haspopup="true"
              aria-expanded={solutionsOpen}
              aria-controls="solutions-dropdown-panel"
            >
              Solutions 
              <ChevronDown 
                className={`w-3.5 h-3.5 transition-transform duration-200 ${
                  solutionsOpen ? 'rotate-180 text-rule-3' : 'text-ink/60'
                }`} 
                aria-hidden="true" 
              />
            </button>
            
            {/* Solutions Dropdown Panel */}
            <div 
              ref={dropdownRef}
              id="solutions-dropdown-panel"
              role="region"
              aria-label="Solutions"
              {...(!solutionsOpen ? { inert: true } : {})}
              className={`absolute top-[82px] left-0 w-full bg-white border-b border-ground-deep shadow-2xl transition-all duration-200 z-50 ${
                solutionsOpen 
                  ? 'opacity-100 translate-y-0 pointer-events-auto' 
                  : 'opacity-0 -translate-y-2 pointer-events-none'
              }`}
            >
              <div className="max-w-7xl mx-auto px-6 lg:px-12 py-8 lg:py-10">
                <div className="grid grid-cols-3 gap-8">
                  {/* Card 1: For Individuals */}
                  <div className="bg-ground-alt/40 border border-ground-deep p-6 flex flex-col justify-between hover:border-rule-3/40 hover:bg-ground-alt/70 transition-all duration-200">
                    <div>
                      <Link 
                        to="/for-individuals" 
                        onClick={() => setSolutionsOpen(false)}
                        className="group block"
                      >
                        <div className="w-10 h-10 bg-white border border-ground-deep flex items-center justify-center text-ink group-hover:text-rule-3 transition-colors mb-4">
                          <User className="w-5 h-5" aria-hidden="true" />
                        </div>
                        <h3 className="font-serif text-base font-semibold text-ink group-hover:text-rule-3 transition-colors flex items-center justify-between">
                          For Individuals
                          <ArrowRight className="w-4 h-4 text-rule-3 opacity-0 -translate-x-1 group-hover:opacity-100 group-hover:translate-x-0 transition-all" aria-hidden="true" />
                        </h3>
                        <p className="text-xs text-ink/70 mt-2 leading-relaxed font-light">
                          Experience FIVE LENS Enneagram coaching.
                        </p>
                      </Link>
                    </div>
                    <div className="pt-4 mt-6 border-t border-ground-deep space-y-2">
                      <Link 
                        to="/for-individuals#request-intro" 
                        onClick={() => setSolutionsOpen(false)}
                        className="text-xs font-medium text-ink/80 hover:text-rule-3 transition-colors flex items-center gap-1.5"
                      >
                        <span className="w-1.5 h-1.5 rounded-full bg-rule-3/80" aria-hidden="true" />
                        Request a Debrief
                      </Link>
                      <Link 
                        to="/find-a-practitioner" 
                        onClick={() => setSolutionsOpen(false)}
                        className="text-xs font-medium text-ink/80 hover:text-rule-3 transition-colors flex items-center gap-1.5"
                      >
                        <span className="w-1.5 h-1.5 rounded-full bg-rule-3/80" aria-hidden="true" />
                        Find a Coach
                      </Link>
                    </div>
                  </div>

                  {/* Card 2: For Coaches and Practitioners */}
                  <div className="bg-ground-alt/40 border border-ground-deep p-6 flex flex-col justify-between hover:border-rule-3/40 hover:bg-ground-alt/70 transition-all duration-200">
                    <div>
                      <Link 
                        to="/practitioner-certification" 
                        onClick={() => setSolutionsOpen(false)}
                        className="group block"
                      >
                        <div className="w-10 h-10 bg-white border border-ground-deep flex items-center justify-center text-ink group-hover:text-rule-3 transition-colors mb-4">
                          <Award className="w-5 h-5" aria-hidden="true" />
                        </div>
                        <h3 className="font-serif text-base font-semibold text-ink group-hover:text-rule-3 transition-colors flex items-center justify-between">
                          For Coaches and Practitioners
                          <ArrowRight className="w-4 h-4 text-rule-3 opacity-0 -translate-x-1 group-hover:opacity-100 group-hover:translate-x-0 transition-all" aria-hidden="true" />
                        </h3>
                        <p className="text-xs text-ink/70 mt-2 leading-relaxed font-light">
                          Add FIVE LENS Enneagram to your practice.
                        </p>
                      </Link>
                    </div>
                    <div className="pt-4 mt-6 border-t border-ground-deep space-y-2">
                      <Link 
                        to="/cohorts" 
                        onClick={() => setSolutionsOpen(false)}
                        className="text-xs font-medium text-ink/80 hover:text-rule-3 transition-colors flex items-center gap-1.5"
                      >
                        <span className="w-1.5 h-1.5 rounded-full bg-rule-3/80" aria-hidden="true" />
                        Upcoming cohorts
                      </Link>
                      <Link 
                        to="/icf-cce-points" 
                        onClick={() => setSolutionsOpen(false)}
                        className="text-xs font-medium text-ink/80 hover:text-rule-3 transition-colors flex items-center gap-1.5"
                      >
                        <span className="w-1.5 h-1.5 rounded-full bg-rule-3/80" aria-hidden="true" />
                        ICF CCE points
                      </Link>
                    </div>
                  </div>

                  {/* Card 3: For Organizations & HR Teams */}
                  <div className="bg-ground-alt/40 border border-ground-deep p-6 flex flex-col justify-between hover:border-rule-3/40 hover:bg-ground-alt/70 transition-all duration-200">
                    <div>
                      <Link 
                        to="/organizations" 
                        onClick={() => setSolutionsOpen(false)}
                        className="group block"
                      >
                        <div className="w-10 h-10 bg-white border border-ground-deep flex items-center justify-center text-ink group-hover:text-rule-3 transition-colors mb-4">
                          <Building2 className="w-5 h-5" aria-hidden="true" />
                        </div>
                        <h3 className="font-serif text-base font-semibold text-ink group-hover:text-rule-3 transition-colors flex items-center justify-between">
                          {t('nav.organizations', 'For Organisations & HR Teams')}
                          <ArrowRight className="w-4 h-4 text-rule-3 opacity-0 -translate-x-1 group-hover:opacity-100 group-hover:translate-x-0 transition-all" aria-hidden="true" />
                        </h3>
                        <p className="text-xs text-ink/70 mt-2 leading-relaxed font-light">
                          Diagnose and develop your teams.
                        </p>
                      </Link>
                    </div>
                    <div className="pt-4 mt-6 border-t border-ground-deep space-y-2">
                      <Link 
                        to="/team-effectiveness" 
                        onClick={() => setSolutionsOpen(false)}
                        className="text-xs font-medium text-ink/80 hover:text-rule-3 transition-colors flex items-center gap-1.5"
                      >
                        <span className="w-1.5 h-1.5 rounded-full bg-rule-3/80" aria-hidden="true" />
                        Team Alignment
                      </Link>
                      <Link 
                        to="/personal-mastery-and-resilience" 
                        onClick={() => setSolutionsOpen(false)}
                        className="text-xs font-medium text-ink/80 hover:text-rule-3 transition-colors flex items-center gap-1.5"
                      >
                        <span className="w-1.5 h-1.5 rounded-full bg-rule-3/80" aria-hidden="true" />
                        Personal Mastery
                      </Link>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Primary Nav Links */}
          {primaryNavLinks.map((link) => (
            <Link
              key={link.href}
              to={link.href}
              className="text-[13px] font-medium uppercase tracking-widest text-ink/70 hover:text-ink transition-colors"
            >
              {link.label}
            </Link>
          ))}
          
          <a
            href="https://portal.enneainternational.com"
            className="text-[13px] font-medium uppercase tracking-widest text-ink/70 hover:text-ink transition-colors whitespace-nowrap"
          >
            Portal Login
          </a>

          <a 
            href={links.bookingUrl}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => handleDiscoveryCallClick(links.bookingUrl)}
            className="bg-ink text-white px-6 py-2.5 uppercase tracking-wider text-xs font-bold hover:bg-ink/90 transition-colors rounded-none shadow-sm whitespace-nowrap inline-flex items-center justify-center"
          >
            Book a Discovery Call
          </a>
        </nav>

        {/* Mobile Menu Toggle */}
        <div className="flex items-center gap-4 md:hidden z-50">
          {links.enableLanguageSelector !== false && (
            <select 
              value={i18n.language} 
              onChange={changeLanguage}
              className="bg-transparent text-xs font-bold uppercase tracking-wider text-ink/70 outline-none cursor-pointer"
              aria-label="Select Language"
            >
              <option value="en">EN</option>
              <option value="es">ES</option>
              <option value="fr">FR</option>
              <option value="zu">ZU</option>
              <option value="xh">XH</option>
              <option value="af">AF</option>
            </select>
          )}
          <button
            className="text-ink p-1"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            aria-label="Toggle mobile menu"
            aria-expanded={mobileMenuOpen}
            aria-controls="mobile-menu"
          >
            {mobileMenuOpen ? <X size={28} aria-hidden="true" /> : <Menu size={28} aria-hidden="true" />}
          </button>
        </div>
      </div>

      {/* Mobile Full-Screen Navigation */}
      <AnimatePresence>
        {mobileMenuOpen && (
          <motion.div
            id="mobile-menu"
            role="dialog"
            aria-modal="true"
            aria-label="Mobile Navigation"
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 top-[82px] w-full h-[calc(100vh-82px)] bg-ground overflow-y-auto z-40 md:hidden flex flex-col justify-between p-6 sm:p-8"
          >
            <div className="space-y-8 pb-8">
              {/* Solutions Section with 3 Cards Stacked */}
              <div>
                <span className="text-[11px] font-bold uppercase tracking-widest text-ink/50 block mb-4">
                  Solutions
                </span>
                <div className="space-y-4">
                  {/* Card 1: For Individuals */}
                  <div className="bg-ground-alt/60 border border-ground-deep p-5">
                    <Link
                      to="/for-individuals"
                      onClick={() => setMobileMenuOpen(false)}
                      className="group block"
                    >
                      <div className="flex items-center gap-3 mb-1.5">
                        <div className="w-8 h-8 bg-white border border-ground-deep flex items-center justify-center text-ink group-hover:text-rule-3 transition-colors shrink-0">
                          <User className="w-4 h-4" />
                        </div>
                        <h3 className="font-serif text-base font-semibold text-ink group-hover:text-rule-3 transition-colors flex items-center justify-between flex-1">
                          For Individuals
                          <ArrowRight className="w-4 h-4 text-rule-3" />
                        </h3>
                      </div>
                      <p className="text-xs text-ink/70 leading-relaxed font-light pl-11">
                        Experience FIVE LENS Enneagram coaching.
                      </p>
                    </Link>
                    <div className="pt-3 mt-3 ml-11 border-t border-ground-deep space-y-2">
                      <Link
                        to="/for-individuals#request-intro"
                        onClick={() => setMobileMenuOpen(false)}
                        className="text-xs font-medium text-ink/80 hover:text-rule-3 transition-colors flex items-center gap-1.5"
                      >
                        <span className="w-1.5 h-1.5 rounded-full bg-rule-3/80" />
                        Request a Debrief
                      </Link>
                      <Link
                        to="/find-a-practitioner"
                        onClick={() => setMobileMenuOpen(false)}
                        className="text-xs font-medium text-ink/80 hover:text-rule-3 transition-colors flex items-center gap-1.5"
                      >
                        <span className="w-1.5 h-1.5 rounded-full bg-rule-3/80" />
                        Find a Coach
                      </Link>
                    </div>
                  </div>

                  {/* Card 2: For Coaches and Practitioners */}
                  <div className="bg-ground-alt/60 border border-ground-deep p-5">
                    <Link
                      to="/practitioner-certification"
                      onClick={() => setMobileMenuOpen(false)}
                      className="group block"
                    >
                      <div className="flex items-center gap-3 mb-1.5">
                        <div className="w-8 h-8 bg-white border border-ground-deep flex items-center justify-center text-ink group-hover:text-rule-3 transition-colors shrink-0">
                          <Award className="w-4 h-4" />
                        </div>
                        <h3 className="font-serif text-base font-semibold text-ink group-hover:text-rule-3 transition-colors flex items-center justify-between flex-1">
                          For Coaches and Practitioners
                          <ArrowRight className="w-4 h-4 text-rule-3" />
                        </h3>
                      </div>
                      <p className="text-xs text-ink/70 leading-relaxed font-light pl-11">
                        Add FIVE LENS Enneagram to your practice.
                      </p>
                    </Link>
                    <div className="pt-3 mt-3 ml-11 border-t border-ground-deep space-y-2">
                      <Link
                        to="/cohorts"
                        onClick={() => setMobileMenuOpen(false)}
                        className="text-xs font-medium text-ink/80 hover:text-rule-3 transition-colors flex items-center gap-1.5"
                      >
                        <span className="w-1.5 h-1.5 rounded-full bg-rule-3/80" />
                        Upcoming cohorts
                      </Link>
                      <Link
                        to="/icf-cce-points"
                        onClick={() => setMobileMenuOpen(false)}
                        className="text-xs font-medium text-ink/80 hover:text-rule-3 transition-colors flex items-center gap-1.5"
                      >
                        <span className="w-1.5 h-1.5 rounded-full bg-rule-3/80" />
                        ICF CCE points
                      </Link>
                    </div>
                  </div>

                  {/* Card 3: For Organizations & HR Teams */}
                  <div className="bg-ground-alt/60 border border-ground-deep p-5">
                    <Link
                      to="/organizations"
                      onClick={() => setMobileMenuOpen(false)}
                      className="group block"
                    >
                      <div className="flex items-center gap-3 mb-1.5">
                        <div className="w-8 h-8 bg-white border border-ground-deep flex items-center justify-center text-ink group-hover:text-rule-3 transition-colors shrink-0">
                          <Building2 className="w-4 h-4" />
                        </div>
                        <h3 className="font-serif text-base font-semibold text-ink group-hover:text-rule-3 transition-colors flex items-center justify-between flex-1">
                          {t('nav.organizations', 'For Organisations & HR Teams')}
                          <ArrowRight className="w-4 h-4 text-rule-3" />
                        </h3>
                      </div>
                      <p className="text-xs text-ink/70 leading-relaxed font-light pl-11">
                        Diagnose and develop your teams.
                      </p>
                    </Link>
                    <div className="pt-3 mt-3 ml-11 border-t border-ground-deep space-y-2">
                      <Link
                        to="/team-effectiveness"
                        onClick={() => setMobileMenuOpen(false)}
                        className="text-xs font-medium text-ink/80 hover:text-rule-3 transition-colors flex items-center gap-1.5"
                      >
                        <span className="w-1.5 h-1.5 rounded-full bg-rule-3/80" />
                        Team Alignment
                      </Link>
                      <Link
                        to="/personal-mastery-and-resilience"
                        onClick={() => setMobileMenuOpen(false)}
                        className="text-xs font-medium text-ink/80 hover:text-rule-3 transition-colors flex items-center gap-1.5"
                      >
                        <span className="w-1.5 h-1.5 rounded-full bg-rule-3/80" />
                        Personal Mastery
                      </Link>
                    </div>
                  </div>
                </div>
              </div>

              {/* Other Primary Navigation Links */}
              <div>
                <span className="text-[11px] font-bold uppercase tracking-widest text-ink/50 block mb-3">
                  Explore
                </span>
                <div className="grid grid-cols-2 gap-3">
                  {primaryNavLinks.map((link) => (
                    <Link
                      key={link.href}
                      to={link.href}
                      onClick={() => setMobileMenuOpen(false)}
                      className="p-3.5 bg-ground-alt/60 border border-ground-deep text-xs font-bold uppercase tracking-wider text-ink hover:text-rule-3 transition-colors block text-center"
                    >
                      {link.label}
                    </Link>
                  ))}
                </div>
              </div>

              {/* Primary Call to Action */}
              <div>
                <Button 
                  variant="primary" 
                  className="w-full h-12" 
                  onClick={() => { 
                    handleDiscoveryCallClick(links.bookingUrl);
                    setMobileMenuOpen(false); 
                    window.open(links.bookingUrl, '_blank', 'noopener,noreferrer'); 
                  }}
                >
                  Book a discovery call
                </Button>
              </div>
            </div>

            {/* Portal Login (Visually separated from Solutions and prospect actions) */}
            <div className="pt-6 border-t border-ground-deep mt-auto bg-ground">
              <a
                href="https://portal.enneainternational.com"
                onClick={() => setMobileMenuOpen(false)}
                className="p-4 bg-ground-alt border border-ground-deep flex items-center justify-between text-xs font-bold uppercase tracking-widest text-ink hover:text-rule-3 transition-colors"
              >
                <span>Portal Login</span>
                <ArrowRight className="w-4 h-4 text-rule-3" />
              </a>
              <p className="text-[11px] text-ink/50 mt-2 px-1">
                Accredited practitioner member portal & tools
              </p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Scroll Progress Indicator */}
      <motion.div
        className="absolute bottom-0 left-0 right-0 h-[2px] bg-rule-3 origin-left z-50"
        style={{ scaleX }}
      />
    </header>
  );
}
